import type { TableContainerHandlers } from '@any-ui-react/table';
import { useResizeObserver } from '@mantine/hooks';
import {
  type CSSProperties,
  type RefAttributes,
  type RefObject,
  type TableHTMLAttributes,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/*
  Add new table and tHead that fixed on the top when showFloatingHeader === true
  1. Set new table width same with origin one
  2. Set th width same with th width from original one
  3. Trigger scroll horizontal from a table will trigger scroll to another
*/
export const useFloatingHeader = () => {
  const [showFloatingHeader, setShowFloatingHeader] = useState(false);
  const [floatingContainerWidth, setFloatingContainerWidth] = useState(0);
  const [tableRef, _rect] = useResizeObserver<HTMLTableElement>();
  const floatingHeaderRef = useRef<HTMLTableElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const floatingContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<TableContainerHandlers>(null);
  const [thWidths, setThWidths] = useState<number[]>([]);
  const [ignoreScrollEvents, setIgnoreScrollEvents] = useState(false);

  const tdHeight = useMemo(() => {
    const value = 0;
    if (tableRef.current) {
      return (tableRef.current.childNodes[1]?.childNodes[0]?.childNodes[0] as HTMLElement).getBoundingClientRect()
        .height;
    }

    return value;
  }, [tableRef.current]);

  // Toggle on of Floating header
  const toggleFloatingHeader = useCallback(
    ({ top, bottom }: { top: number; bottom: number }) => {
      // When scrolling from top to bottom and
      // the last 2td row is visible enough, floating header will be triggered.
      setShowFloatingHeader(top <= 0 && bottom > tdHeight * 2);

      // Set floating table header width same as the container one
      if (scrollContainerRef.current) {
        setFloatingContainerWidth(scrollContainerRef.current.offsetWidth);
      }
    },
    [tdHeight],
  );

  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        toggleFloatingHeader(tableRef.current.getBoundingClientRect());
      }

      // Sync scroll horizontal
      if (scrollContainerRef.current && floatingHeaderRef.current) {
        floatingHeaderRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [tableRef, toggleFloatingHeader]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      setFloatingContainerWidth(scrollContainerRef.current.offsetWidth);
    }

    const widths = Array.from(tableRef.current?.querySelector('thead tr')?.childNodes ?? []).map(
      th => (th as HTMLElement).getBoundingClientRect().width,
    );
    setThWidths(widths);
  }, [tableRef]);

  const synScroll = useCallback(
    (e: UIEvent<HTMLElement>, targetRef: RefObject<HTMLElement>) => {
      const ignore = ignoreScrollEvents;
      setIgnoreScrollEvents(false);
      if (ignore) {
        return;
      }

      if (targetRef.current) {
        setIgnoreScrollEvents(true);
        targetRef.current.scrollLeft = (e.target as HTMLElement).scrollLeft;
      }
      if (tableContainerRef.current) {
        // This will trigger sticky column to show shadow
        tableContainerRef.current.handleScroll(e);
      }
    },
    [ignoreScrollEvents],
  );

  const thProps = useCallback(
    (index: number): { style: CSSProperties } => ({
      style: {
        minWidth: `${thWidths[index]}px`,
        maxWidth: `${thWidths[index]}px`,
      },
    }),
    [thWidths],
  );

  const floatingContainerRefProps = useMemo(
    (): TableHTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement> => ({
      ref: floatingContainerRef,
      style: {
        width: `${floatingContainerWidth}px`,
      },
    }),
    [floatingContainerWidth],
  );

  const floatingHeaderRefProps = useMemo(
    (): TableHTMLAttributes<HTMLTableElement> & RefAttributes<HTMLTableElement> => ({
      ref: floatingHeaderRef,
      onScroll: (e: UIEvent<HTMLElement>) => synScroll(e, scrollContainerRef),
      style: {
        borderCollapse: 'collapse',
        display: 'block',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        width: '100%',
      } as CSSProperties,
    }),
    [synScroll],
  );

  const scrollContainerRefProps = useMemo(
    (): TableHTMLAttributes<HTMLDivElement> & RefAttributes<HTMLDivElement> => ({
      ref: scrollContainerRef,
      onScroll: (e: UIEvent<HTMLElement>) => synScroll(e, floatingHeaderRef),
    }),
    [synScroll],
  );

  return {
    floatingContainerRefProps,
    floatingHeaderRefProps,
    scrollContainerRefProps,
    tableContainerRef,
    tableRef,
    showFloatingHeader,
    thProps,
  };
};
